import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Shield, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { adminApi } from '../../api';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import '../../styles/ScoringRules.css';

export const ScoringRules: React.FC = () => {
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [statistics, setStatistics] = useState<any>(null);

  // Form state
  const [formData, setFormData] = useState({
    rule_name: '',
    rule_category: 'debt_to_income',
    rule_description: '',
    condition_type: 'range',
    min_value: '',
    max_value: '',
    exact_value: '',
    score_points: '',
    is_active: true,
    priority: 0,
  });

  const categories = [
    { value: 'debt_to_income', label: 'Debt-to-Income Ratio' },
    { value: 'income', label: 'Income Level' },
    { value: 'employment', label: 'Employment Status' },
    { value: 'credit_history', label: 'Credit History' },
    { value: 'collateral', label: 'Collateral' },
  ];

  const conditionTypes = [
    { value: 'range', label: 'Range (Min-Max)' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'equals', label: 'Equals' },
  ];

  useEffect(() => {
    fetchRules();
    fetchStatistics();
  }, [selectedCategory]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getScoringRules(selectedCategory || undefined);
      if (response.success && response.data) {
        setRules(response.data.rules || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load scoring rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await adminApi.getScoringStatistics();
      if (response.success && response.data) {
        setStatistics(response.data);
      }
    } catch (err: any) {
      console.error('Failed to load statistics:', err);
    }
  };

  const handleCreateRule = async () => {
    try {
      setError('');
      const response = await adminApi.createScoringRule({
        ...formData,
        min_value: formData.min_value ? parseFloat(formData.min_value) : null,
        max_value: formData.max_value ? parseFloat(formData.max_value) : null,
        exact_value: formData.exact_value ? parseFloat(formData.exact_value) : null,
        score_points: parseInt(formData.score_points),
        priority: parseInt(formData.priority.toString()),
      });

      if (response.success) {
        setSuccessMessage('Scoring rule created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchRules();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create scoring rule');
    }
  };

  const handleUpdateRule = async () => {
    if (!editingRule) return;

    try {
      setError('');
      const response = await adminApi.updateScoringRule(editingRule.rule_id, {
        rule_name: formData.rule_name,
        rule_description: formData.rule_description,
        min_value: formData.min_value ? parseFloat(formData.min_value) : null,
        max_value: formData.max_value ? parseFloat(formData.max_value) : null,
        exact_value: formData.exact_value ? parseFloat(formData.exact_value) : null,
        score_points: parseInt(formData.score_points),
        is_active: formData.is_active,
        priority: parseInt(formData.priority.toString()),
      });

      if (response.success) {
        setSuccessMessage('Scoring rule updated successfully');
        setEditingRule(null);
        resetForm();
        fetchRules();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update scoring rule');
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this scoring rule?')) return;

    try {
      setError('');
      const response = await adminApi.deleteScoringRule(ruleId);
      if (response.success) {
        setSuccessMessage('Scoring rule deactivated successfully');
        fetchRules();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate scoring rule');
    }
  };

  const openEditModal = (rule: any) => {
    setEditingRule(rule);
    setFormData({
      rule_name: rule.rule_name,
      rule_category: rule.rule_category,
      rule_description: rule.rule_description || '',
      condition_type: rule.condition_type,
      min_value: rule.min_value?.toString() || '',
      max_value: rule.max_value?.toString() || '',
      exact_value: rule.exact_value?.toString() || '',
      score_points: rule.score_points.toString(),
      is_active: rule.is_active,
      priority: rule.priority || 0,
    });
  };

  const resetForm = () => {
    setFormData({
      rule_name: '',
      rule_category: 'debt_to_income',
      rule_description: '',
      condition_type: 'range',
      min_value: '',
      max_value: '',
      exact_value: '',
      score_points: '',
      is_active: true,
      priority: 0,
    });
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingRule(null);
    resetForm();
    setError('');
  };

  if (loading) {
    return (
      <div className="scoring-rules-page">
        <div style={{ padding: '4rem', textAlign: 'center' }}>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="scoring-rules-page">
      <div className="settings-section-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2>🛡️ Credit Scoring Rules</h2>
            <p>Configure and manage automated credit scoring rules</p>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
            <Plus size={20} />
            Create Rule
          </button>
        </div>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {successMessage && (
        <div className="success-message">
          <CheckCircle size={20} />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <div className="statistics-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#dbeafe' }}>
              <Shield size={24} style={{ color: '#3b82f6' }} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Rules</div>
              <div className="stat-value">{statistics.total_rules || 0}</div>
              <div className="stat-meta">Active scoring rules</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#d1fae5' }}>
              <CheckCircle size={24} style={{ color: '#10b981' }} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Categories</div>
              <div className="stat-value">{statistics.total_categories || 0}</div>
              <div className="stat-meta">Rule categories</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#fef3c7' }}>
              <TrendingUp size={24} style={{ color: '#f59e0b' }} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Max Score</div>
              <div className="stat-value">{statistics.max_possible_score || 0}</div>
              <div className="stat-meta">Maximum possible score</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#dbeafe' }}>
              <TrendingUp size={24} style={{ color: '#3b82f6' }} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Avg Points</div>
              <div className="stat-value">{statistics.avg_points_per_rule ? statistics.avg_points_per_rule.toFixed(1) : 0}</div>
              <div className="stat-meta">Per rule</div>
            </div>
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="filter-section">
        <label>Filter by Category:</label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="category-filter"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      {/* Rules Table */}
      <div className="rules-table-container">
        <table className="rules-table">
          <thead>
            <tr>
              <th>Rule Name</th>
              <th>Category</th>
              <th>Condition</th>
              <th>Points</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                  No scoring rules found
                </td>
              </tr>
            ) : (
              rules.map((rule) => (
                <tr key={rule.rule_id}>
                  <td>
                    <div className="rule-name">{rule.rule_name}</div>
                    {rule.rule_description && (
                      <div className="rule-description">{rule.rule_description}</div>
                    )}
                  </td>
                  <td>
                    <span className="category-badge">
                      {categories.find((c) => c.value === rule.rule_category)?.label || rule.rule_category}
                    </span>
                  </td>
                  <td>
                    <div className="condition-text">
                      {rule.condition_type === 'range' && (
                        <span>{rule.min_value} - {rule.max_value}</span>
                      )}
                      {rule.condition_type === 'greater_than' && (
                        <span>&gt; {rule.min_value}</span>
                      )}
                      {rule.condition_type === 'less_than' && (
                        <span>&lt; {rule.max_value}</span>
                      )}
                      {rule.condition_type === 'equals' && (
                        <span>= {rule.exact_value}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="score-points">{rule.score_points > 0 ? '+' : ''}{rule.score_points}</span>
                  </td>
                  <td>{rule.priority}</td>
                  <td>
                    {rule.is_active ? (
                      <span className="badge badge-success">Active</span>
                    ) : (
                      <span className="badge badge-secondary">Inactive</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => openEditModal(rule)}
                        className="btn btn-sm btn-secondary"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.rule_id)}
                        className="btn btn-sm btn-danger"
                        title="Deactivate"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingRule) && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRule ? 'Edit Scoring Rule' : 'Create Scoring Rule'}</h3>
              <button onClick={closeModal} className="close-button">×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Rule Name *</label>
                <input
                  type="text"
                  value={formData.rule_name}
                  onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
                  placeholder="e.g., Low DTI Bonus"
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.rule_category}
                  onChange={(e) => setFormData({ ...formData, rule_category: e.target.value })}
                  disabled={!!editingRule}
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.rule_description}
                  onChange={(e) => setFormData({ ...formData, rule_description: e.target.value })}
                  rows={2}
                  placeholder="Describe what this rule checks..."
                />
              </div>

              <div className="form-group">
                <label>Condition Type *</label>
                <select
                  value={formData.condition_type}
                  onChange={(e) => setFormData({ ...formData, condition_type: e.target.value })}
                  disabled={!!editingRule}
                >
                  {conditionTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                {(formData.condition_type === 'range' || formData.condition_type === 'greater_than') && (
                  <div className="form-group">
                    <label>Min Value</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.min_value}
                      onChange={(e) => setFormData({ ...formData, min_value: e.target.value })}
                    />
                  </div>
                )}

                {(formData.condition_type === 'range' || formData.condition_type === 'less_than') && (
                  <div className="form-group">
                    <label>Max Value</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.max_value}
                      onChange={(e) => setFormData({ ...formData, max_value: e.target.value })}
                    />
                  </div>
                )}

                {formData.condition_type === 'equals' && (
                  <div className="form-group">
                    <label>Exact Value</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.exact_value}
                      onChange={(e) => setFormData({ ...formData, exact_value: e.target.value })}
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Score Points *</label>
                  <input
                    type="number"
                    value={formData.score_points}
                    onChange={(e) => setFormData({ ...formData, score_points: e.target.value })}
                    placeholder="e.g., 50"
                  />
                </div>

                <div className="form-group">
                  <label>Priority</label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={closeModal} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={editingRule ? handleUpdateRule : handleCreateRule}
                className="btn btn-primary"
              >
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
